import Button from 'ext-ui/components/Button/Button'
import React, { ReactElement, useLayoutEffect, useRef } from 'react'
import { useTranslate } from 'src/localization/Trns'

import type { CardDetailsAvailability } from '../cardDetailHeroMapping'
import SvgEye from '../icons/SvgEye'
import SvgEyeOff from '../icons/SvgEyeOff'
import SvgSnowflake from '../icons/SvgSnowflake'
import * as style from './CardDisplayActions.style'

type CardDisplayActionsProps = {
  /**
   * Exact capability-gated action the button will execute, and the sole gate on
   * whether it renders at all. `undefined` means no freeze action applies to
   * this card right now — either the status has no freeze move (PENDING_ISSUANCE,
   * LOCKED, CANCELED, or a status still resolving) or the matching capability is
   * not granted — and there is then no button, which is the same rule the
   * redesigned settings tile applies to its freeze row.
   */
  freezeAction: 'freeze' | 'unfreeze' | undefined
  /** Runs the capability-gated freeze or unfreeze mutation. */
  onFreeze: () => void
  canFreeze: boolean
  freezePending: boolean
  /** Reveal-disclosure state, owned by the hero shell. */
  revealOpen: boolean
  onToggleReveal: () => void
  cardDetailsAvailability: CardDetailsAvailability | undefined
  cardDetailsCapabilityError: boolean
  /**
   * The reveal panel's DOM id, generated with `useId` by whichever variant owns
   * this cluster (the shell for `full`, the compact bar for `compact`) and shared
   * with its `CardRevealPanel`, so the Show details trigger's `aria-controls`
   * resolves to the right panel while both heroes are mounted.
   */
  revealPanelId: string
}

/**
 * The card-display action cluster in the hero toolbar: Show details (the reveal
 * disclosure trigger) then Freeze. The `•••` menu joins in slice 3.
 *
 * The freeze label is derived from the lifecycle controller's exact action so
 * copy and behavior cannot diverge while the display-status query refreshes.
 * That action also decides whether the button exists: an always-rendered button
 * fell back to the "Freeze" label whenever no action applied, so a FROZEN card
 * without `UNFREEZE_PHYSICAL_CARD` offered an inert "Freeze" in the header while
 * the settings tile — reading the same controller — showed no freeze row at all.
 */
const CardDisplayActions: React.FC<CardDisplayActionsProps> = ({
  freezeAction,
  onFreeze,
  canFreeze,
  freezePending,
  revealOpen,
  onToggleReveal,
  cardDetailsAvailability,
  cardDetailsCapabilityError,
  revealPanelId,
}): ReactElement => {
  const translate = useTranslate()

  const showFreeze = freezeAction !== undefined
  const freezeLabel =
    freezeAction === 'unfreeze' ? translate('Unfreeze') : translate('Freeze')

  // READ_ONLY is a grant: revealing details is itself a read-only operation.
  const canReveal =
    cardDetailsAvailability === 'AVAILABLE' ||
    cardDetailsAvailability === 'READ_ONLY'
  const capabilityPending = cardDetailsAvailability === undefined
  // Everything that is neither a grant nor still pending hides the reveal
  // affordance — today UNAVAILABLE and UNKNOWN — as does a capability failure.
  const capabilityUnavailable =
    cardDetailsCapabilityError || (!canReveal && !capabilityPending)

  // `disabled` would remove the button from the tab order, so neither `aria-busy`
  // nor the loading label could ever be reached by a keyboard or screen-reader
  // user — the state would exist without being conveyed, which `src/CLAUDE.md`'s
  // WCAG 2.1 AA requirement does not allow. `aria-disabled` keeps it focusable
  // and readable; the click guard below keeps it un-activatable.
  const busy = !revealOpen && capabilityPending

  const triggerVisible = !capabilityUnavailable
  const actionsRef = useRef<HTMLDivElement>(null)
  const triggerHadFocusRef = useRef(false)
  const freezeHadFocusRef = useRef(false)

  // Freezing from the header can be the very thing that removes this button:
  // the card lands on FROZEN, no `UNFREEZE_PHYSICAL_CARD` is granted, and the
  // control the user just activated unmounts under their focus — dropping a
  // keyboard user to `<body>` at the top of the page. Focus moves left to the
  // sibling that is still there, for the same reason the reveal trigger does it
  // below.
  //
  // The reveal trigger is queried out of the container rather than held by a
  // ref: a forwarded ref on the ext-ui Button forces its legacy design-system
  // channel (see `CardActionsMenu`). `aria-expanded` is the semantic marker —
  // the trigger is the only node in this cluster that carries it — so focus
  // recovery never depends on a test attribute.
  useLayoutEffect(() => {
    if (showFreeze || !freezeHadFocusRef.current) return
    freezeHadFocusRef.current = false
    const revealTrigger =
      actionsRef.current?.querySelector<HTMLButtonElement>('[aria-expanded]')
    revealTrigger?.focus()
  }, [showFreeze])

  // The trigger UNMOUNTS when the capability resolves to a refusal or supplies
  // no card-scoped verdict. Removing a focused element drops focus to `<body>`,
  // stranding a keyboard user at the top of the page mid-task. When Freeze is
  // still present, focus moves there; if this was the last action, normal DOM
  // focus fallback applies because there is no meaningful local target.
  //
  // Focus is tracked with onFocus/onBlur rather than read from
  // `document.activeElement` here — by the time this effect runs the button is
  // already gone and the answer would always be `<body>`. A blur is not
  // guaranteed to fire on unmount, and that is precisely the case that has to
  // resolve to `true`.
  //
  // Only covers a user who had reached the trigger. Someone who never focused
  // it is not mid-interaction and keeps their current focus.
  useLayoutEffect(() => {
    if (triggerVisible || !triggerHadFocusRef.current) return
    triggerHadFocusRef.current = false
    actionsRef.current?.querySelector<HTMLButtonElement>('button')?.focus()
  }, [triggerVisible])

  return (
    <div css={style.actions} ref={actionsRef}>
      {triggerVisible && (
        <Button
          variant="secondary"
          iconLeft={
            revealOpen ? (
              <SvgEyeOff width={15} height={15} />
            ) : (
              <SvgEye width={15} height={15} />
            )
          }
          css={style.ariaDisabled}
          onClick={() => {
            if (!busy) onToggleReveal()
          }}
          onFocus={() => {
            triggerHadFocusRef.current = true
          }}
          onBlur={() => {
            triggerHadFocusRef.current = false
          }}
          aria-disabled={busy}
          aria-busy={busy}
          aria-expanded={revealOpen}
          aria-controls={revealOpen ? revealPanelId : undefined}
          data-testid="card-show-details-button"
        >
          {revealOpen
            ? translate('Hide details')
            : capabilityPending
              ? translate('Loading secure card details')
              : translate('Show details')}
        </Button>
      )}
      {showFreeze && (
        <Button
          variant="secondary"
          iconLeft={<SvgSnowflake width={15} height={15} />}
          onClick={onFreeze}
          onFocus={() => {
            freezeHadFocusRef.current = true
          }}
          onBlur={() => {
            freezeHadFocusRef.current = false
          }}
          disabled={!canFreeze || freezePending}
          loading={freezePending}
          aria-busy={freezePending}
          data-testid="card-freeze-button"
        >
          {freezeLabel}
        </Button>
      )}
    </div>
  )
}

export default CardDisplayActions
